import * as React from 'react';
import i18n from '@/utils/i18n';
import {
  calcPackageCount,
  isOnboarding,
  MerchantOrderStatus,
  MerchantOrderType,
  NotChangeableMerchantOrderStatus,
  Revision,
} from './HandleStatusChange';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import {
  pickpackTypesByVytalType,
  VytalStoreTypes,
} from '../../views/MerchantComponents/StoreTypeSelector';
import { ContentSection } from '../Layout/ContentSection';
import { ContainerType } from '../../views/ContainerComponents/ContainerPage';
import { Communicator } from '../../tools/Communication';

/**
 * Only to be used in EditMerchantOrder
 */
export function OrderDetailsEditItem(props: {
  updateRevision: (
    attrName: string,
    attrValue: string | number,
    storeType?: string,
  ) => void;
  ogRevisionStatus: number;
  revision: Revision;
  orderId: string | null;
  earliestOrderExecutionDate: Date;
  dpdTrackingCode: string | null;
  canAccessPartnerData: boolean;
  isFirstOrder: boolean | null;
  storeType: string | null;
  availableReturnOptions?: number[];
  extraTypes?: number[];
}) {
  const {
    updateRevision,
    availableReturnOptions = [],
    canAccessPartnerData,
    dpdTrackingCode,
    earliestOrderExecutionDate,
    isFirstOrder,
    ogRevisionStatus,
    orderId,
    revision,
    storeType,
    extraTypes = [],
  } = props;

  const [containerTypes, setContainerTypes] = React.useState<ContainerType[]>(
    [],
  );

  React.useEffect(() => {
    Communicator.Get('/Container/GetAllContainerCategories', {})
      .then((result) => result.json())
      .then((data) => {
        if (Array.isArray(data.containerTypes)) {
          setContainerTypes(data.containerTypes);
        }
      });
  }, []);

  const dpdReturnOptions = [
    MerchantOrderType.DPDShopReturn,
    MerchantOrderType.DPDPickup,
  ];
  const returnOptions = [...dpdReturnOptions, MerchantOrderType.Pickup];
  function orderTypeDisabled(type: number) {
    if (
      returnOptions.includes(type) &&
      !availableReturnOptions.includes(type)
    ) {
      return true;
    }
    return false;
  }

  function orderTypeSelectionDisabled() {
    const type = parseInt(revision.type as string, 10);
    if (orderId != null && dpdReturnOptions.includes(type)) {
      return true;
    }
    const status = parseInt(revision.status as string, 10);
    return ![
      MerchantOrderStatus.CreatedForReview,
      MerchantOrderStatus.ApprovedForPackageDelivery,
      MerchantOrderStatus.ApprovedForLocalDelivery,
      MerchantOrderStatus.CreatedForReviewPickshare,
    ].includes(status);
  }

  function getPackageWeight(i: number) {
    if (revision.packageWeights == null) {
      return '';
    }
    if (revision.packageWeights.length <= i) {
      return '';
    }
    return revision.packageWeights[i] || '';
  }

  return (
    <>
      <ContentSection title={i18n.t('MerchantOrder:orderDetails')}>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('MerchantOrder:orderType')}
          </span>
          <select
            className="form-control custom-select"
            onChange={(e) => updateRevision('type', e.target.value)}
            disabled={orderTypeSelectionDisabled()}
            value={revision.type}
          >
            {Object.keys(MerchantOrderType)
              .filter((t) => {
                const type = (MerchantOrderType as any)[t];
                if (parseInt(revision.type as string, 10) === type) {
                  return true;
                }
                if (extraTypes.includes(type)) {
                  return true;
                }
                // show never (existing orders of these types still render via
                // the current-type check above). EventsSample is deprecated and
                // must not be selectable for new orders. Pickup is refused by
                // both order endpoints, and Both submits as a Pickup plus a
                // Resupply, so neither can produce an order any more.
                if (
                  type === MerchantOrderType.Onboarding ||
                  type === MerchantOrderType.Undefined ||
                  type === MerchantOrderType.Termination ||
                  type === MerchantOrderType.EventsSample ||
                  type === MerchantOrderType.Pickup ||
                  type === MerchantOrderType.Both
                ) {
                  return false;
                }
                // only resupply or return for non admins
                if (
                  !canAccessPartnerData &&
                  type !== MerchantOrderType.Resupply &&
                  !returnOptions.includes(type)
                ) {
                  return false;
                }
                // allow special sample return options and onboarding and no others
                // when store type is restaurant and it is the first order
                if (
                  pickpackTypesByVytalType[VytalStoreTypes.Restaurant].includes(
                    storeType || 'CANTEEN',
                  ) &&
                  isFirstOrder &&
                  type !== MerchantOrderType.Special &&
                  type !== MerchantOrderType.Sample &&
                  !returnOptions.includes(type) &&
                  !isOnboarding(type)
                ) {
                  return false;
                }
                // allow pickup, special, sample, dpd pickup / return and resupply but no others
                // if store type is restaurant and it is NOT the first order
                if (
                  pickpackTypesByVytalType[VytalStoreTypes.Restaurant].includes(
                    storeType || 'CANTEEN',
                  ) &&
                  isFirstOrder === false &&
                  type !== MerchantOrderType.Special &&
                  type !== MerchantOrderType.Sample &&
                  !returnOptions.includes(type) &&
                  type !== MerchantOrderType.Resupply &&
                  type !== MerchantOrderType.WarehouseTransfer
                ) {
                  return false;
                }
                if (
                  dpdReturnOptions.includes(type) &&
                  NotChangeableMerchantOrderStatus.includes(ogRevisionStatus)
                ) {
                  return false;
                }
                return true;
              })
              .map((t) => (
                <option
                  disabled={orderTypeDisabled((MerchantOrderType as any)[t])}
                  value={(MerchantOrderType as any)[t]}
                >
                  {i18n.t(
                    `MerchantOrder:Type:${(MerchantOrderType as any)[t]}`,
                  )}
                </option>
              ))}
          </select>
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('MerchantOrder:executionDate')}
          </span>
          <input
            className="form-control"
            type="date"
            min={earliestOrderExecutionDate.toISOString().substring(0, 10)}
            onChange={(e) =>
              updateRevision('orderExecutionDate', e.target.value)
            }
            value={revision.orderExecutionDate.toISOString().substring(0, 10)}
            disabled={NotChangeableMerchantOrderStatus.includes(
              ogRevisionStatus,
            )}
          />
        </div>
        {!isNullOrWhitespace(dpdTrackingCode) && (
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('MerchantOrder:dpdTrackingCodes')}
            </span>
            <input
              className="form-control"
              type="text"
              value={dpdTrackingCode as any}
              disabled
            />
          </div>
        )}
        {canAccessPartnerData && (
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('Common:status')}
            </span>
            <select
              className="form-control custom-select"
              onChange={(e) => updateRevision('status', e.target.value)}
              value={revision.status}
              disabled={
                orderId != null &&
                NotChangeableMerchantOrderStatus.includes(ogRevisionStatus)
              }
            >
              {Object.entries(MerchantOrderStatus)
                .filter(([key, value]) =>
                  // For mass order creator, only show CreatedForReview and Rejected (Partnermanagement)
                  orderId == null
                    ? [
                        MerchantOrderStatus.CreatedForReview, // 0_CreatedForReview
                        MerchantOrderStatus.Rejected, // 2.3_Partnermanagement
                      ].includes(value)
                    : [
                        MerchantOrderStatus.CreatedForReview,
                        MerchantOrderStatus.ApprovedForLocalDelivery,
                        MerchantOrderStatus.ApprovedForPackageDelivery,
                        MerchantOrderStatus.ApprovedForPickshareDelivery,
                        MerchantOrderStatus.Completed,
                        MerchantOrderStatus.Rejected,
                        MerchantOrderStatus.Deleted,
                        MerchantOrderStatus.Invalid,
                        MerchantOrderStatus.PlannedOnTour,
                        MerchantOrderStatus.OnHold,
                      ].includes(value) ||
                      (MerchantOrderStatus as any)[key] === revision.status,
                )
                .sort((a, b) => {
                  const aId = (MerchantOrderStatus as any)[a[0]];
                  const bId = (MerchantOrderStatus as any)[b[0]];
                  const aLabel = i18n.t(`MerchantOrder:Status:${aId}`);
                  const bLabel = i18n.t(`MerchantOrder:Status:${bId}`);
                  return aLabel.localeCompare(bLabel);
                })
                .map(([key, value]) => (
                  <option value={value}>
                    {i18n.t(`MerchantOrder:Status:${value}`)}
                  </option>
                ))}
            </select>
          </div>
        )}
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('MerchantOrder:deliveryNoteLang')}
          </span>
          <select
            className="form-control custom-select"
            value={revision.language}
            disabled={NotChangeableMerchantOrderStatus.includes(
              ogRevisionStatus,
            )}
            onChange={(e) => updateRevision('language', e.target.value)}
          >
            <option value={'de'}>{i18n.t('Common:german')}</option>
            <option value={'fr'}>{i18n.t('Common:french')}</option>
            <option value={'nl'}>{i18n.t('Common:dutch')}</option>
            <option value={'en'}>{i18n.t('Common:english')}</option>
          </select>
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('MerchantOrder:comments')}
          </span>
          <textarea
            className="form-control"
            placeholder={
              canAccessPartnerData
                ? i18n.t('MerchantOrder:commentsPlaceholder')
                : ''
            }
            color="danger"
            value={revision.comment}
            disabled={NotChangeableMerchantOrderStatus.includes(
              ogRevisionStatus,
            )}
            onChange={(e) => updateRevision('comment', e.target.value)}
          />
        </div>
        {canAccessPartnerData && (
          <div className="input-group mb-3">
            <span className="input-group-text" id="basic-addon1">
              {i18n.t('MerchantOrder:internalComment')}
            </span>
            <textarea
              className="form-control"
              placeholder={i18n.t('MerchantOrder:internalCommentPlaceholder')}
              value={revision.internalComment}
              onChange={(e) =>
                updateRevision('internalComment', e.target.value)
              }
            />
          </div>
        )}

        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('MerchantOrder:parcelSendBackCount')}
          </span>
          <input
            disabled={
              ![
                MerchantOrderType.DPDShopReturn,
                MerchantOrderType.DPDPickup,
              ].includes(parseInt(revision.type as string, 10)) ||
              (NotChangeableMerchantOrderStatus.includes(ogRevisionStatus) &&
                ogRevisionStatus !== MerchantOrderStatus.ReadyToSendByPartner)
            }
            className="form-control"
            placeholder={i18n.t('MerchantOrder:parcelSendBackCount')}
            value={revision.parcelCount}
            onChange={(e) =>
              updateRevision(
                'parcelCount',
                e.target.value.replace(/[^0-9.]/g, ''),
              )
            }
          />
        </div>
      </ContentSection>
      <ContentSection
        title={i18n.t('MerchantOrder:packageWeights')}
        collapsable
        collapsed
      >
        {Array.from(
          { length: calcPackageCount(revision, containerTypes) },
          (_, i) => (
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {`${i18n.t('MerchantOrder:packageWeight')}  ${i + 1}`}
              </span>
              <input
                className="form-control"
                placeholder={'7 kg'}
                value={getPackageWeight(i)}
                onChange={(e) =>
                  updateRevision(
                    `packageWeights.${i}`,
                    e.target.value.replace(/[^0-9.]/g, ''),
                  )
                }
              />
            </div>
          ),
        )}
      </ContentSection>
    </>
  );
}
